import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  createUpdateByCodTdp,
  createUpdateByCodTurma,
  createUpdateByCodAlunoTurma,
  findByTdpCodigoAndNumeroBimestre,
  saveAusenciaCompensada,
} from "../api-aluno-frequencia";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";
import LoadingDialog from "../../UI/LoadingDialog";
import AusenciaCompensadaForm from "./AusenciaCompensadaForm";

const BIMESTRES = [1, 2, 3, 4];
// Frequência mínima exigida (LDB): abaixo disso o aluno é destacado.
const FREQUENCIA_MINIMA = 75;

const FrequenciaCell = ({ frequencia }) => {
  if (frequencia === null || frequencia === undefined) {
    return <Typography variant="body2" color="text.disabled">--</Typography>;
  }
  if (Number(frequencia) < FREQUENCIA_MINIMA) {
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        icon={<WarningAmberIcon />}
        label={`${frequencia}%`}
        title={`Abaixo de ${FREQUENCIA_MINIMA}%`}
      />
    );
  }
  return <Typography variant="body2">{frequencia}%</Typography>;
};

export default function AusenciaCompensadaTable({ tdp, isAdmin }) {
  const [numeroBimestre, setNumeroBimestre] = useState(1);
  const [alunosFrequencia, setAlunosFrequencia] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  // Valores digitados na coluna "Ausência Compensada", por código do registro.
  const [rascunhos, setRascunhos] = useState({});
  const [savingCodigo, setSavingCodigo] = useState(null);
  const [editando, setEditando] = useState(null);
  const [isSavingDialog, setIsSavingDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [processando, setProcessando] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setRascunhos({});
    findByTdpCodigoAndNumeroBimestre(tdp.codigo, numeroBimestre)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
          setAlunosFrequencia([]);
        } else if (Array.isArray(data)) {
          setAlunosFrequencia(
            [...data].sort((a, b) =>
              (a.alunoTurma?.aluno?.nome ?? "").localeCompare(b.alunoTurma?.aluno?.nome ?? "", "pt-BR"),
            ),
          );
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tdp.codigo, numeroBimestre, reloadKey]);

  const replaceRow = (data) => {
    setAlunosFrequencia((prevState) => prevState.map((af) => (af.codigo === data.codigo ? data : af)));
  };

  const persistir = (persist) =>
    saveAusenciaCompensada(persist).then((data) => {
      if (!data) return null;
      if (data.error) {
        errorMessage(data.message);
        return null;
      }
      replaceRow(data);
      return data;
    });

  const salvarAusencia = (af) => {
    const valor = rascunhos[af.codigo];
    if (valor === undefined) return;
    if (valor === "" || !/^\d+$/.test(valor)) {
      errorToast("Informe um número inteiro para a ausência compensada.");
      return;
    }
    setSavingCodigo(af.codigo);
    persistir({ ...af, ausenciaCompensada: Number(valor) })
      .then((data) => {
        if (!data) return;
        setRascunhos(({ [af.codigo]: _, ...resto }) => resto);
        success(`Salvo: ${af.alunoTurma?.aluno?.nome ?? "aluno"}.`);
      })
      .finally(() => setSavingCodigo(null));
  };

  const salvarDialog = (persist) => {
    setIsSavingDialog(true);
    persistir(persist)
      .then((data) => {
        if (!data) return;
        setEditando(null);
        success("Salvo com sucesso.");
      })
      .finally(() => setIsSavingDialog(false));
  };

  const executarEmLote = (request, mensagem) => {
    setAnchorEl(null);
    setEditando(null);
    confirmAlert({
      title: "Alerta",
      message: mensagem,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            setProcessando(true);
            request()
              .then((data) => {
                if (data?.error) {
                  errorMessage(data.message);
                  return;
                }
                success("Operação realizada com sucesso.");
                setReloadKey((key) => key + 1);
              })
              .finally(() => setProcessando(false));
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const renderTabela = () => {
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando frequências..." />
        </Box>
      );
    }
    if (alunosFrequencia.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={6}>
          Nenhum registro de frequência para este bimestre.
        </Typography>
      );
    }
    return (
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" sx={{ minWidth: 640 }} aria-label="Frequência dos alunos">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
              <TableCell>Aluno</TableCell>
              <TableCell align="center">Aulas</TableCell>
              <TableCell align="center">Faltas</TableCell>
              <TableCell align="center" sx={{ width: 190 }}>
                Ausência Compensada
              </TableCell>
              <TableCell align="center">Frequência</TableCell>
              {isAdmin && <TableCell sx={{ width: 56 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {alunosFrequencia.map((af) => {
              const rascunho = rascunhos[af.codigo];
              const alterado = rascunho !== undefined && rascunho !== String(af.ausenciaCompensada ?? "");
              const salvando = savingCodigo === af.codigo;
              return (
                <TableRow key={af.codigo} hover>
                  <TableCell>{af.alunoTurma?.aluno?.nome}</TableCell>
                  <TableCell align="center">{af.quantidadeAulas ?? "--"}</TableCell>
                  <TableCell align="center">{af.quantidadeFaltas ?? "--"}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                      <TextField
                        size="small"
                        value={rascunho ?? String(af.ausenciaCompensada ?? "")}
                        onChange={(event) =>
                          setRascunhos((prevState) => ({
                            ...prevState,
                            [af.codigo]: event.target.value.replace(/\D/g, "").slice(0, 4),
                          }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            salvarAusencia(af);
                          }
                        }}
                        disabled={salvando}
                        slotProps={{
                          htmlInput: {
                            inputMode: "numeric",
                            "aria-label": `Ausência compensada de ${af.alunoTurma?.aluno?.nome ?? "aluno"}`,
                            style: { textAlign: "center" },
                          },
                        }}
                        sx={{ width: 80 }}
                      />
                      <Box width={34}>
                        {salvando ? (
                          <CircularProgress size={18} />
                        ) : (
                          alterado && (
                            <Tooltip title="Salvar (Enter)">
                              <IconButton size="small" color="primary" onClick={() => salvarAusencia(af)}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <FrequenciaCell frequencia={af.frequencia} />
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Tooltip title="Editar todos os campos">
                        <IconButton size="small" onClick={() => setEditando(af)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <LoadingDialog
        openDialog={processando}
        setOpenDialog={setProcessando}
        title="Aluno Frequência"
        description="Criando/Atualizando registros..."
      />
      <AusenciaCompensadaForm
        alunoFrequencia={editando}
        onClose={() => setEditando(null)}
        onSave={salvarDialog}
        isSaving={isSavingDialog}
        onCreateUpdateAlunoTurma={(af) =>
          executarEmLote(
            () => createUpdateByCodAlunoTurma(af.alunoTurma.codigo),
            "Deseja criar/atualizar os registros de frequência deste aluno?",
          )
        }
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={numeroBimestre}
          onChange={(event, value) => value && setNumeroBimestre(value)}
          aria-label="Bimestre"
        >
          {BIMESTRES.map((b) => (
            <ToggleButton key={b} value={b}>
              {b}º Bimestre
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {isAdmin && (
          <>
            <Tooltip title="Ações de administrador">
              <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem
                onClick={() =>
                  executarEmLote(
                    () => createUpdateByCodTurma(tdp.turma.codigo),
                    "Deseja criar/atualizar os registros de frequência de toda a turma?",
                  )
                }
              >
                Criar/Atualizar por Turma
              </MenuItem>
              <MenuItem
                onClick={() =>
                  executarEmLote(
                    () => createUpdateByCodTdp(tdp.codigo),
                    "Deseja criar/atualizar os registros de frequência desta turma - disciplina?",
                  )
                }
              >
                Criar/Atualizar por Turma - Disciplina
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" component="div" mb={1}>
        Digite a ausência compensada e pressione Enter (ou ✓) para salvar.
      </Typography>

      {renderTabela()}
    </Box>
  );
}
